import { FLAGS, type FlagKey } from '@gozoki/feature-flags'
import {
  Boxes,
  CalendarRange,
  Layers,
  PackageSearch,
  TrendingUp,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import type { PermissionRequirement, RouteTo } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'

/** Lecture partagée par les quatre écrans de prévision hébergés par la sandbox. */
const FORECAST_READ: PermissionRequirement = ['read', 'previsions.previsions.forecast']

/**
 * Garde de l'écran Correction des ruptures, de sa carte et, côté serveur, de ses routes et de leur
 * périmètre boutique : la lecture des prévisions ne l'ouvre pas (décision du 24/10).
 */
export const SALES_CORRECTION_READ: PermissionRequirement = [
  'read',
  'previsions.corrections.correction',
]

export interface SandboxTool {
  title: string
  description: string
  icon: LucideIcon
  to: RouteTo
  flagKey?: FlagKey
  /** Garde de l'écran visé — reprise du `staticData.permission` de sa route. */
  permission: PermissionRequirement
  guideHref?: string
}

export const SANDBOX_TOOLS: SandboxTool[] = [
  {
    title: 'Prévisions long terme',
    description:
      'Chiffre d’affaires, UVC et tonnage par boutique et par mois à 5 ans : année 1 par l’hebdo, puis taux de croissance.',
    icon: TrendingUp,
    to: '/previsions-long-terme',
    permission: FORECAST_READ,
    flagKey: FLAGS.CAForecasting,
    guideHref: '/guide-previsions-long-terme.pdf',
  },
  {
    title: 'Prévisions court terme UVC',
    description: 'UVC produit × boutique de 1 à 7 jours, backtest et saison.',
    icon: Boxes,
    to: '/previsions-produits',
    permission: FORECAST_READ,
    flagKey: FLAGS.shortTermPrevision,
    guideHref: '/guide-previsions-produits.pdf',
  },
  {
    title: 'Prévisions catégories 31 jours',
    description: 'UVC par catégorie sur un mois, backtest arborescent.',
    icon: Layers,
    to: '/previsions-categories',
    permission: FORECAST_READ,
    flagKey: FLAGS.categoryForecasting31days,
    guideHref: '/guide-previsions-categories.pdf',
  },
  {
    title: 'Prévisions semaine × catégorie',
    description: 'UVC hebdomadaires par catégorie jusqu’à 52 semaines.',
    icon: CalendarRange,
    to: '/previsions-semaine',
    permission: FORECAST_READ,
    flagKey: FLAGS.uvcForecasting52Weeks,
    guideHref: '/guide-previsions-semaine.pdf',
  },
  {
    title: 'Correction des ruptures',
    description: 'Demande reconstituée sur les jours ruptés, report sur les produits de repli.',
    icon: Wrench,
    to: '/correction-ruptures',
    permission: SALES_CORRECTION_READ,
    flagKey: FLAGS.salesUnitsCorrections,
    guideHref: '/note-correction-ruptures.pdf',
  },
  {
    title: 'Contrôle des ruptures',
    description: 'Impact des ruptures sur les ventes, un jour donné, prévision de la veille.',
    icon: PackageSearch,
    to: '/controle-rupture',
    permission: ['read', 'previsions.controle-rupture.stockout-impact'],
  },
]

export const SANDBOX_FLAG_KEYS: (FlagKey | undefined)[] = SANDBOX_TOOLS.map((tool) => tool.flagKey)

/**
 * Garde de l'index de la sandbox et de son entrée de menu : sa ressource propre ou la garde de l'un
 * de ses outils.
 */
export const SANDBOX_PAGE_READ: PermissionRequirement = moduleIndexPermission([
  ['read', 'previsions.sandbox.sandbox'],
  ...SANDBOX_TOOLS.map((tool) => tool.permission),
])
